import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Medal, Trophy, Shield, Star, Award } from 'lucide-react';
import styles from './CommendationsDisplay.module.css';
import axios from '../api/axios';

interface Commendation {
  id: number;
  type: number;
  typeName: string;
  category: number;
  categoryName: string;
  title: string;
  description: string;
  missionTitle?: string;
  eventTitle?: string;
  awardedByUsername?: string;
  awardedDate: string;
  iconUrl?: string;
  metricsData?: string;
  displayOrder: number;
}

interface CommendationSummary {
  category: number;
  categoryName: string;
  count: number;
  commendations: Commendation[];
}

interface Props {
  memberId: number;
  onCountChange?: (count: number) => void;
}

const categoryIcons: { [key: number]: React.ReactNode } = {
  1: <Shield className={styles.categoryIcon} />, // Industrial/Mining
  2: <Shield className={styles.categoryIcon} />, // Salvage/Recovery
  3: <Trophy className={styles.categoryIcon} />, // Logistics/Hauling
  4: <Star className={styles.categoryIcon} />, // Engineering/Construction
  5: <Award className={styles.categoryIcon} />, // Medical/Rescue
  6: <Shield className={styles.categoryIcon} />, // Security/Escort
  7: <Star className={styles.categoryIcon} />, // Recon/Intel
  8: <Trophy className={styles.categoryIcon} />, // Administration
  9: <Award className={styles.categoryIcon} />, // Recruitment/Growth
  10: <Medal className={styles.categoryIcon} />, // Training/Mentorship
  11: <Star className={styles.categoryIcon} />, // Exploration/Charting
  12: <Trophy className={styles.categoryIcon} />, // Special Prestige
};

export const CommendationsDisplay: React.FC<Props> = ({ memberId, onCountChange }) => {
  const [summary, setSummary] = useState<CommendationSummary[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedCommendation, setSelectedCommendation] = useState<Commendation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCommendations();
  }, [memberId]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedCommendation) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [selectedCommendation]);

  const fetchCommendations = async () => {
    try {
      const response = await axios.get(`/commendations/member/${memberId}/summary`);
      setSummary(response.data);
      const totalCount = response.data.reduce((acc: number, s: CommendationSummary) => acc + s.count, 0);
      if (onCountChange) {
        onCountChange(totalCount);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading commendations:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load commendations');
      setLoading(false);
    }
  };

  const getCategoryColor = (category: number): string => {
    const colors: { [key: number]: string } = {
      1: '#ff9500', // Industrial/Mining - Orange
      2: '#8b6914', // Salvage/Recovery - Brown
      3: '#4a90e2', // Logistics/Hauling - Blue
      4: '#7c3aed', // Engineering/Construction - Purple
      5: '#dc2626', // Medical/Rescue - Red
      6: '#059669', // Security/Escort - Green
      7: '#0891b2', // Recon/Intel - Cyan
      8: '#6366f1', // Administration - Indigo
      9: '#ec4899', // Recruitment/Growth - Pink
      10: '#f59e0b', // Training/Mentorship - Amber
      11: '#3b82f6', // Exploration/Charting - Sky Blue
      12: '#fbbf24', // Special Prestige - Gold
    };
    return colors[category] || '#6b7280';
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Medal className={styles.loadingIcon} />
        <p>Loading commendations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>Error loading commendations: {error}</p>
      </div>
    );
  }

  if (summary.length === 0) {
    return (
      <div className={styles.empty}>
        <Medal className={styles.emptyIcon} />
        <p>No commendations earned yet</p>
      </div>
    );
  }

  const displayedCommendations = selectedCategory
    ? summary.find(s => s.category === selectedCategory)?.commendations || []
    : summary.flatMap(s => s.commendations);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          <Medal className={styles.titleIcon} />
          Commendations
        </h2>
      </div>

      {/* Category filters */}
      <div className={styles.categoryFilters}>
        <button
          className={`${styles.categoryButton} ${selectedCategory === null ? styles.active : ''}`}
          onClick={() => setSelectedCategory(null)}
        >
          All Categories
        </button>
        {summary.map(s => (
          <button
            key={s.category}
            className={`${styles.categoryButton} ${selectedCategory === s.category ? styles.active : ''}`}
            onClick={() => setSelectedCategory(s.category)}
            style={{
              borderColor: selectedCategory === s.category ? getCategoryColor(s.category) : 'transparent'
            }}
          >
            {categoryIcons[s.category]}
            <span>{s.categoryName.replace(/_/g, ' ')}</span>
            <span className={styles.categoryCount}>{s.count}</span>
          </button>
        ))}
      </div>

      {/* Commendations grid */}
      <div className={styles.grid}>
        {displayedCommendations.map(comm => (
          <div
            key={comm.id}
            className={styles.card}
            onClick={() => setSelectedCommendation(comm)}
          >
            <div className={styles.cardIcon}>
              {comm.iconUrl ? (
                <img 
                  src={comm.iconUrl} 
                  alt={comm.title}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = '<img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0ZjQ2ZTUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjYiLz48cGF0aCBkPSJNMTUuNDc3IDEyLjg5IDEyIDE1bC0zLjQ3Ny0yLjExdjYuMjFsLjA5NiAxLjg0IDMuMzgxIDEuNjkxIDMuMzgxLTEuNjkxLjA5Ni0xLjg0eiIvPjwvc3ZnPg==" style="width: 42px; height: 42px; object-fit: contain;" />';
                  }}
                />
              ) : (
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0ZjQ2ZTUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjYiLz48cGF0aCBkPSJNMTUuNDc3IDEyLjg5IDEyIDE1bC0zLjQ3Ny0yLjExdjYuMjFsLjA5NiAxLjg0IDMuMzgxIDEuNjkxIDMuMzgxLTEuNjkxLjA5Ni0xLjg0eiIvPjwvc3ZnPg=="
                  alt="Award Ribbon"
                  style={{ width: '42px', height: '42px', objectFit: 'contain' }}
                />
              )}
            </div>
            <div className={styles.compactInfo}>
              <h3>{comm.title.replace(/([A-Z])/g, ' $1').trim()}</h3>
              <span className={styles.categoryBadge}>
                {comm.categoryName.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {selectedCommendation && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setSelectedCommendation(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setSelectedCommendation(null)}>
              ×
            </button>
            
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                {selectedCommendation.iconUrl ? (
                  <img 
                    src={selectedCommendation.iconUrl} 
                    alt={selectedCommendation.title}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0ZjQ2ZTUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjYiLz48cGF0aCBkPSJNMTUuNDc3IDEyLjg5IDEyIDE1bC0zLjQ3Ny0yLjExdjYuMjFsLjA5NiAxLjg0IDMuMzgxIDEuNjkxIDMuMzgxLTEuNjkxLjA5Ni0xLjg0eiIvPjwvc3ZnPg==" style="width: 56px; height: 56px; object-fit: contain;" />';
                    }}
                  />
                ) : (
                  <img 
                    src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM0ZjQ2ZTUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48Y2lyY2xlIGN4PSIxMiIgY3k9IjgiIHI9IjYiLz48cGF0aCBkPSJNMTUuNDc3IDEyLjg5IDEyIDE1bC0zLjQ3Ny0yLjExdjYuMjFsLjA5NiAxLjg0IDMuMzgxIDEuNjkxIDMuMzgxLTEuNjkxLjA5Ni0xLjg0eiIvPjwvc3ZnPg=="
                    alt="Award Ribbon"
                    style={{ width: '56px', height: '56px', objectFit: 'contain' }}
                  />
                )}
              </div>
              <div className={styles.modalTitle}>
                <h2>{selectedCommendation.title.replace(/([A-Z])/g, ' $1').trim()}</h2>
                <span className={styles.categoryBadge}>
                  {selectedCommendation.categoryName.replace(/_/g, ' ')}
                </span>
              </div>
            </div>

            <p className={styles.modalDescription}>{selectedCommendation.description}</p>

            <div className={styles.modalMeta}>
              {selectedCommendation.missionTitle && (
                <div className={styles.metaItem}>
                  <Trophy className={styles.metaIcon} />
                  <span>Mission: {selectedCommendation.missionTitle}</span>
                </div>
              )}
              {selectedCommendation.eventTitle && (
                <div className={styles.metaItem}>
                  <Star className={styles.metaIcon} />
                  <span>Event: {selectedCommendation.eventTitle}</span>
                </div>
              )}
              {selectedCommendation.awardedByUsername && (
                <div className={styles.metaItem}>
                  <Award className={styles.metaIcon} />
                  <span>Awarded by {selectedCommendation.awardedByUsername}</span>
                </div>
              )}
              <div className={styles.metaItem}>
                <span className={styles.date}>
                  {new Date(selectedCommendation.awardedDate).toLocaleDateString()}
                </span>
              </div>
            </div>

            {selectedCommendation.metricsData && (
              <div className={styles.metrics}>
                <code>{selectedCommendation.metricsData}</code>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
